/**
 * cart-gwp.js — McD enforcement/API and gift quantity-control presentation.
 * Tier/product gifts and WWT belong exclusively to gwp-progress-bar.js.
 * Mystery merchandising and MYSTERY10 belong to cart-mystery-upsell.js.
 * Load globally with defer, before cart-mystery-upsell.js.
 */
(() => {
  'use strict';
  if (window.__WOOBLES_CART_GWP_MANAGER_INIT__) return;
  window.__WOOBLES_CART_GWP_MANAGER_INIT__ = true;
  const MCD = {
    // Existing separate redemption forms already supply these variant IDs.
    // Never infer prize vs reward from the code prefix (both may start with M).
    rulesByVariantId: {
      56860353396902: {
        name: 'Mr Monopoly prize',
        type: 'prize',
      },
      46653206495398: {
        name: 'Canadian bracelet reward',
        type: 'reward',
      },
    },
    // Reward minimum purchase ($30) and customer eligibility remain native
    // Shopify discount rules. Do not duplicate currency conversion here.
    // Reward codes do NOT grant free shipping. Normal shipping rules apply.
    debug: true,
    officialGiftProperty: '_mcd_code_gated_gift',
    checkoutGiftProperty: '_gwp-checkout-ui-gift',
    codeProperty: '_mcd_discount_code',
    prefixProperty: '_mcd_discount_prefix',
  };
  const MCD_VERIFY_TRIES = 5;
  const MCD_VERIFY_DELAY_MS = 350;
  const VERIFY_AFTER_WRITE_MS = 350;
  const RECONCILE_DELAY_MS = 350;
  const mcdKnownGiftVariantIds = new Set(Object.keys(MCD.rulesByVariantId));
  const rawShopifyRoot = window.Shopify?.routes?.root || '/';
  const shopifyRoot = rawShopifyRoot.endsWith('/') ? rawShopifyRoot : `${rawShopifyRoot}/`;
  const CART_ENDPOINTS = {
    get: `${shopifyRoot}cart.js`,
    add: `${shopifyRoot}cart/add.js`,
    update: `${shopifyRoot}cart/update.js`,
    checkout: `${shopifyRoot}checkout`,
  };
  const warn = (...args) => console.warn('[McD GWP]', ...args);
  const error = (...args) => console.error('[McD GWP]', ...args);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  function registerMcdGiftVariant(variantId) {
    if (variantId) mcdKnownGiftVariantIds.add(String(variantId));
  }
  let cartMutationTail = Promise.resolve();
  let latestProtectionCart = null;
  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });
    let data = null;
    try {
      data = await response.json();
    } catch {
      // Some Shopify error responses may not contain JSON.
    }
    if (!response.ok) {
      const detail = data?.description || data?.message || '';
      const suffix = detail ? `: ${detail}` : '';
      throw new Error(`${url} responded with ${response.status}${suffix}`);
    }
    return data;
  }
  function enqueueCartMutation(operation) {
    const queued = cartMutationTail.then(operation, operation);
    // Keep the queue usable after an individual mutation fails.
    cartMutationTail = queued.catch(() => undefined);
    return queued;
  }
  async function fetchStableCart() {
    await cartMutationTail;
    const cart = await requestJson(CART_ENDPOINTS.get);
    latestProtectionCart = cart;
    protectGiftQuantities();
    return cart;
  }
  function addItems(items) {
    const normalizedItems = (items || [])
      .map((item) => ({
        ...item,
        id: Number(item.id),
        quantity: Math.max(1, Number(item.quantity || 1)),
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);
    if (!normalizedItems.length) return Promise.resolve(null);
    return enqueueCartMutation(() =>
      requestJson(CART_ENDPOINTS.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: normalizedItems }),
      }),
    );
  }
  function getNativeCartRenderContext() {
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (
      !cartItems ||
      typeof cartItems.getSectionsToRender !== 'function' ||
      typeof cartItems.getSectionInnerHTML !== 'function'
    ) {
      return null;
    }
    try {
      const sections = cartItems
        .getSectionsToRender()
        .filter((section) => section?.id && section?.section && section?.selector);
      return sections.length ? { cartItems, sections } : null;
    } catch (nativeCartError) {
      warn('Unable to prepare native cart section refresh', nativeCartError);
      return null;
    }
  }
  function renderNativeCartSections(cart, context) {
    if (!context || !cart?.sections) return;
    const { cartItems, sections } = context;
    sections.forEach((section) => {
      const sectionHtml = cart.sections[section.section];
      if (typeof sectionHtml !== 'string') return;
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      try {
        const html = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
        if (typeof updateCartMarkup === 'function') updateCartMarkup(target, html);
        else target.innerHTML = html;
      } catch (nativeCartError) {
        warn(`Unable to refresh native cart section ${section.section}`, nativeCartError);
      }
    });
    const isEmpty = Number(cart.item_count || 0) === 0;
    cartItems.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
  }
  function updateCart({ updates = null, discount = undefined } = {}) {
    const body = {};
    const nativeCartRenderContext = getNativeCartRenderContext();
    if (updates && Object.keys(updates).length) {
      body.updates = updates;
    }
    if (discount !== undefined) {
      body.discount = discount;
    }
    if (!Object.keys(body).length) {
      return Promise.resolve(null);
    }
    if (nativeCartRenderContext) {
      body.sections = nativeCartRenderContext.sections.map((section) => section.section);
      body.sections_url = window.location.pathname;
    }
    return enqueueCartMutation(async () => {
      const cart = await requestJson(CART_ENDPOINTS.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      latestProtectionCart = cart;
      renderNativeCartSections(cart, nativeCartRenderContext);
      protectGiftQuantities();
      return cart;
    });
  }
  function getCartItems(cart) {
    return Array.isArray(cart?.items) ? cart.items : [];
  }
  function getLinesByVariant(cart, variantId) {
    const target = String(variantId);
    return getCartItems(cart).filter((item) => String(item.variant_id) === target);
  }
  function hasQualifyingMerchandise(cart) {
    return getCartItems(cart).some((item) => {
      if (isGiftOnlyLine(item)) return false;
      return Number(item.quantity || 0) > 0;
    });
  }
  function getQualifyingSubtotalCents(cart) {
    const itemsSubtotal = Number(cart?.items_subtotal_price || 0);
    const giftLineTotal = getCartItems(cart).reduce((sum, item) => {
      if (!isGiftOnlyLine(item)) return sum;
      return sum + Number(item.final_line_price ?? item.line_price ?? 0);
    }, 0);
    return Math.max(0, itemsSubtotal - giftLineTotal);
  }
  function mergeLineUpdate(updates, lineKey, quantity) {
    if (!lineKey) return;
    updates[String(lineKey)] = Number(quantity);
  }
  function normalizeCode(code) {
    return String(code || '')
      .trim()
      .toUpperCase();
  }
  function getCartDiscountCodes(cart) {
    return Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
  }
  function getAppliedDiscountCodeStrings(cart) {
    return getCartDiscountCodes(cart)
      .map((discount) => normalizeCode(discount?.code))
      .filter(Boolean);
  }
  function getDiscountEntryForCode(cart, code) {
    const normalized = normalizeCode(code);
    return getCartDiscountCodes(cart).find((entry) => normalizeCode(entry?.code) === normalized) || null;
  }
  function discountStringWithCode(cart, code) {
    const normalized = normalizeCode(code);
    const codes = getAppliedDiscountCodeStrings(cart);
    if (normalized) codes.push(normalized);
    return [...new Set(codes)].join(',');
  }
  function discountStringWithoutCode(cart, code) {
    const normalized = normalizeCode(code);
    return getAppliedDiscountCodeStrings(cart)
      .filter((existingCode) => existingCode !== normalized)
      .join(',');
  }
  function normalizePrefix(prefix) {
    return normalizeCode(prefix);
  }
  function isOfficialMcdGiftLine(item) {
    return item?.properties?.[MCD.officialGiftProperty] === 'true';
  }
  function getMcdGiftLines(cart) {
    return getCartItems(cart).filter((item) => {
      const variantId = String(item?.variant_id || '');
      if (isOfficialMcdGiftLine(item)) {
        registerMcdGiftVariant(variantId);
        return true;
      }
      return mcdKnownGiftVariantIds.has(variantId);
    });
  }
  function getMcdLineCode(item) {
    return normalizeCode(item?.properties?.[MCD.codeProperty]);
  }
  function getMcdLinePrefix(item) {
    return normalizePrefix(item?.properties?.[MCD.prefixProperty]);
  }
  function getMcdRule(variantId) {
    return MCD.rulesByVariantId[String(variantId)] || null;
  }
  function getMcdCodeAllocationCents(item, code) {
    const allocations = Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];
    const normalized = normalizeCode(code);
    return allocations.reduce((sum, allocation) => {
      const application = allocation?.discount_application;
      if (application?.type !== 'discount_code' || normalizeCode(application.title) !== normalized) return sum;
      return sum + Math.max(0, Number(allocation.amount) || 0);
    }, 0);
  }
  function getMcdAuthorizationFailure(cart, item) {
    if (!item) return 'gift_missing';
    const rule = getMcdRule(item.variant_id);
    if (!rule) return 'unknown_redemption_variant';
    if (!isOfficialMcdGiftLine(item)) return 'missing_redemption_marker';
    const code = getMcdLineCode(item);
    const prefix = getMcdLinePrefix(item);
    if (!code || !prefix || !code.startsWith(prefix)) return 'invalid_line_code';
    const entry = getDiscountEntryForCode(cart, code);
    if (!entry) return 'code_missing_from_cart';
    if (entry.applicable !== true) return 'code_not_applicable';
    const finalPrice = Number(item.final_line_price);
    if (item.final_line_price == null || !Number.isFinite(finalPrice)) {
      return 'line_price_unavailable';
    }
    if (rule.type === 'prize') {
      // Prize is already $0; its native code supplies free shipping.
      // Do not demand a product discount allocation on a $0 prize.
      return finalPrice === 0 ? null : 'prize_not_free';
    }
    // Reward is a paid product made free by its native product discount.
    // Applicability enforces the configured $30 minimum, customer restrictions,
    // dates and combinations. Product allocations prove this exact code
    // discounts this exact line; a shipping code cannot authorize a reward.
    if (!hasQualifyingMerchandise(cart)) return 'qualifying_merchandise_missing';
    if (!(Number(item.original_line_price) > 0)) return 'reward_already_zero_before_code';
    if (finalPrice !== 0) return 'reward_not_fully_discounted';
    if (!(getMcdCodeAllocationCents(item, code) > 0)) return 'reward_code_allocation_missing';
    return null;
  }
  function isAuthorizedMcdGiftLine(cart, item) {
    return getMcdAuthorizationFailure(cart, item) === null;
  }
  function describeMcdState(cart, { variantId, code }) {
    const item = findMcdGiftForCode(cart, { variantId, code });
    const entry = getDiscountEntryForCode(cart, code);
    const rule = getMcdRule(variantId);
    return {
      variantId,
      redemptionType: rule?.type || null,
      currency: cart?.currency,
      qualifyingSubtotalCents: getQualifyingSubtotalCents(cart),
      codePresent: Boolean(entry),
      codeApplicable: entry?.applicable ?? null,
      giftPresent: Boolean(item),
      quantity: item?.quantity ?? null,
      originalLinePrice: item?.original_line_price ?? null,
      finalLinePrice: item?.final_line_price ?? null,
      submittedCodeAllocationCents: getMcdCodeAllocationCents(item, code),
      failure:
        getMcdAuthorizationFailure(cart, item) || (Number(item?.quantity) !== 1 ? 'gift_quantity_not_one' : null),
    };
  }
  function logMcdState(stage, cart, context) {
    const state = describeMcdState(cart, context);
    if (MCD.debug) console.info('[McD GWP] ' + stage, JSON.stringify(state, null, 2));
    return state;
  }
  function isValidMcdGiftLine(cart, item) {
    return isAuthorizedMcdGiftLine(cart, item) && Number(item.quantity || 0) === 1;
  }
  function buildMcdGiftItem({ variantId, code, prefix }) {
    const normalizedCode = normalizeCode(code);
    const normalizedPrefix = normalizePrefix(prefix);
    const rule = getMcdRule(variantId);
    const properties = {
      [MCD.officialGiftProperty]: 'true',
      [MCD.codeProperty]: normalizedCode,
      [MCD.prefixProperty]: normalizedPrefix,
    };
    // The existing storefront pricing logic treats this property as an
    // instruction to make the line $0. That is correct for the Mr Monopoly
    // prize, whose code supplies free shipping. The bracelet must enter the
    // cart at its normal price so its Buy X Get Y code can discount that exact
    // line and create a verifiable product-discount allocation.
    if (rule?.type === 'prize') {
      properties[MCD.checkoutGiftProperty] = 'true';
    }
    return {
      id: Number(variantId),
      quantity: 1,
      properties,
    };
  }
  function findMcdGiftForCode(cart, { variantId, code }) {
    const targetVariantId = String(variantId);
    const normalizedCode = normalizeCode(code);
    return getMcdGiftLines(cart).find(
      (item) =>
        String(item.variant_id) === targetVariantId &&
        isOfficialMcdGiftLine(item) &&
        getMcdLineCode(item) === normalizedCode,
    );
  }
  function planMcdReconciliation(cart) {
    const updates = {};
    const seenCodeKeys = new Set();
    getMcdGiftLines(cart).forEach((item) => {
      const variantId = String(item.variant_id || '');
      registerMcdGiftVariant(variantId);
      // Promo-only variants added without our redemption marker are not allowed
      // to remain as ordinary cart merchandise.
      if (!isOfficialMcdGiftLine(item)) {
        mergeLineUpdate(updates, item.key, 0);
        return;
      }
      const code = getMcdLineCode(item);
      const dedupeKey = `${variantId}:${code}`;
      if (!isAuthorizedMcdGiftLine(cart, item) || seenCodeKeys.has(dedupeKey)) {
        mergeLineUpdate(updates, item.key, 0);
        return;
      }
      seenCodeKeys.add(dedupeKey);
      if (Number(item.quantity || 0) !== 1) {
        mergeLineUpdate(updates, item.key, 1);
      }
    });
    return { updates };
  }
  async function waitForValidMcdState({ variantId, code, prefix }, initialCart = null, maxTries = MCD_VERIFY_TRIES) {
    const normalizedCode = normalizeCode(code);
    let cart = initialCart;
    for (let attempt = 0; attempt <= maxTries; attempt += 1) {
      if (cart) {
        const discountEntry = getDiscountEntryForCode(cart, normalizedCode);
        const giftLine = findMcdGiftForCode(cart, { variantId, code: normalizedCode });
        if (discountEntry?.applicable === false) return cart;
        if (giftLine && isValidMcdGiftLine(cart, giftLine)) return cart;
      }
      if (attempt === maxTries) break;
      await sleep(MCD_VERIFY_DELAY_MS);
      cart = await fetchStableCart();
    }
    return cart || fetchStableCart();
  }
  async function removeMcdGiftForCode(cart, { variantId, code, removeCode = true }) {
    const normalizedCode = normalizeCode(code);
    const targetVariantId = String(variantId);
    const updates = {};
    getMcdGiftLines(cart).forEach((item) => {
      if (
        String(item.variant_id) === targetVariantId &&
        isOfficialMcdGiftLine(item) &&
        getMcdLineCode(item) === normalizedCode
      ) {
        mergeLineUpdate(updates, item.key, 0);
      }
    });
    return (
      (await updateCart({
        updates: Object.keys(updates).length ? updates : null,
        discount: removeCode ? discountStringWithoutCode(cart, normalizedCode) : undefined,
      })) || cart
    );
  }
  async function redeemMcdPromo({ code, prefix, giftVariantId } = {}) {
    const normalizedCode = normalizeCode(code);
    const normalizedPrefix = normalizePrefix(prefix);
    const variantId = String(giftVariantId || '').trim();
    if (!normalizedCode || !normalizedPrefix || !normalizedCode.startsWith(normalizedPrefix)) {
      return { ok: false, reason: 'invalid_code' };
    }
    if (!/^\d+$/.test(variantId) || !getMcdRule(variantId)) {
      return { ok: false, reason: 'invalid_gift_config' };
    }
    registerMcdGiftVariant(variantId);
    if (exclusiveTransactionInFlight || reconciling) {
      return { ok: false, reason: 'busy' };
    }
    return runExclusiveTransaction(async () => {
      let cart = await fetchStableCart();
      const alreadyValidLine = findMcdGiftForCode(cart, {
        variantId,
        code: normalizedCode,
      });
      if (alreadyValidLine && isValidMcdGiftLine(cart, alreadyValidLine)) {
        scheduleUiRefresh('mcd_redemption_already_valid');
        refreshSlideCart('mcd_redemption_already_valid');
        logMcdState('already valid', cart, { variantId, code: normalizedCode });
        return { ok: true, cart };
      }
      // Clear any stale copy of this exact code+gift pair before adding the
      // authoritative tagged line.
      const staleUpdates = {};
      getMcdGiftLines(cart).forEach((item) => {
        if (
          String(item.variant_id) === variantId &&
          isOfficialMcdGiftLine(item) &&
          getMcdLineCode(item) === normalizedCode
        ) {
          mergeLineUpdate(staleUpdates, item.key, 0);
        }
      });
      if (Object.keys(staleUpdates).length) {
        cart = (await updateCart({ updates: staleUpdates })) || cart;
      }
      await addItems([
        buildMcdGiftItem({
          variantId,
          code: normalizedCode,
          prefix: normalizedPrefix,
        }),
      ]);
      // Preserve other currently-applied codes, then apply the submitted code.
      let updatedCart = await updateCart({
        discount: discountStringWithCode(cart, normalizedCode),
      });
      logMcdState('after applying code, BEFORE cleanup', updatedCart, { variantId, code: normalizedCode });
      updatedCart = await waitForValidMcdState(
        {
          variantId,
          code: normalizedCode,
          prefix: normalizedPrefix,
        },
        updatedCart,
      );
      const giftLine = findMcdGiftForCode(updatedCart, {
        variantId,
        code: normalizedCode,
      });
      if (!giftLine || !isValidMcdGiftLine(updatedCart, giftLine)) {
        // Capture evidence before removing the failed code and line.
        const diagnostics = logMcdState('validation failed, BEFORE cleanup', updatedCart, {
          variantId,
          code: normalizedCode,
        });
        updatedCart = await removeMcdGiftForCode(updatedCart, {
          variantId,
          code: normalizedCode,
          removeCode: true,
        });
        refreshSlideCart('mcd_redemption_invalid_cleanup');
        scheduleReconcile(VERIFY_AFTER_WRITE_MS, 'after_mcd_invalid_cleanup');
        return {
          ok: false,
          reason: 'not_applicable',
          diagnostics,
          cart: updatedCart,
        };
      }
      logMcdState('redemption confirmed', updatedCart, { variantId, code: normalizedCode });
      refreshSlideCart('mcd_redemption_success');
      scheduleUiRefresh('mcd_redemption_success');
      scheduleReconcile(VERIFY_AFTER_WRITE_MS, 'after_mcd_redemption');
      return { ok: true, cart: updatedCart };
    });
  }
  async function refreshCustomDrawer() {
    const response = await fetch(`${shopifyRoot}?sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Unable to refresh cart drawer.');
    const sections = await response.json();
    for (const [id, selector] of [
      ['cart-drawer', '#CartDrawer'],
      ['cart-icon-bubble', '.shopify-section'],
    ]) {
      const parsed = new DOMParser().parseFromString(sections[id] || '', 'text/html').querySelector(selector);
      const target = id === 'cart-drawer' ? document.querySelector(selector) : document.getElementById(id);
      if (!parsed || !target) continue;
      if (typeof updateCartMarkup === 'function') updateCartMarkup(target, parsed.innerHTML);
      else target.innerHTML = parsed.innerHTML;
    }
    scheduleUiRefresh('custom_drawer_refreshed');
  }
  function isCheckoutTrigger(target) {
    if (!(target instanceof Element)) return false;
    const trigger = target.closest("a, button, input[type='submit']");
    if (!trigger) return false;
    const href = trigger.getAttribute('href') || '';
    const name = trigger.getAttribute('name') || '';
    const text = trigger.textContent || '';
    const classes = String(trigger.className || '');
    const haystack = `${href} ${name} ${text} ${classes}`.toLowerCase();
    return haystack.includes('/checkout') || haystack.includes('checkout');
  }

  function isGiftOnlyLine(item) {
    const props = item?.properties || {};
    return (
      mcdKnownGiftVariantIds.has(String(item?.variant_id)) ||
      props._gwp === 'tier' ||
      props._gwp === 'product' ||
      props._wwt_code_gated_gift === 'true' ||
      props[MCD.officialGiftProperty] === 'true' ||
      props[MCD.checkoutGiftProperty] === 'true'
    );
  }
  function scheduleUiRefresh() {
    protectGiftQuantities();
  }
  // UI protection only: the progress engine still owns tier/product/WWT rules.
  const GIFT_ROW_SELECTOR = '.cart-item, [data-cart-item], .cart__row';
  const GIFT_INPUT_SELECTOR = '.quantity__input, input[data-quantity-variant-id]';
  const GIFT_BUTTON_SELECTOR = '.quantity__button, button[name="plus"], button[name="minus"]';
  function giftRowState(row) {
    const input = row.querySelector(GIFT_INPUT_SELECTOR);
    const variantId = String(row.dataset.variantId || input?.dataset.quantityVariantId || '');
    const key = row.dataset.lineKey;
    const items = getCartItems(latestProtectionCart);
    const index = Number(input?.dataset.index || row.dataset.index);
    // A line key distinguishes a free tier Mystery from a paid Mystery upsell.
    const item = key
      ? items.find((line) => line.key === key)
      : Number.isInteger(index) && index > 0
        ? items[index - 1]
        : null;
    const actualVariantId = String(item?.variant_id || variantId);
    const mcd = mcdKnownGiftVariantIds.has(actualVariantId) || row.dataset.mcdGift === 'true';
    // Production WWT ID identifies the gift-only tin on the native cart page.
    // This is presentation protection, not a second WWT validator.
    const wwt = actualVariantId === '56444556050598' || item?.properties?._wwt_code_gated_gift === 'true';
    const otherGift =
      row.dataset.gwp === 'tier' ||
      row.dataset.gwp === 'product' ||
      item?.properties?._gwp === 'tier' ||
      item?.properties?._gwp === 'product' ||
      item?.properties?.[MCD.checkoutGiftProperty] === 'true';
    if (!mcd && !wwt && !otherGift) return null;
    return { quantity: mcd || wwt ? 1 : Math.max(1, Number(item?.quantity || input?.value || 1)) };
  }
  function protectGiftQuantities() {
    document.querySelectorAll(GIFT_ROW_SELECTOR).forEach((row) => {
      const state = giftRowState(row);
      if (!state) return;
      row.querySelectorAll(GIFT_INPUT_SELECTOR).forEach((input) => {
        input.readOnly = true;
        input.value = String(state.quantity);
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('min', String(state.quantity));
        input.setAttribute('max', String(state.quantity));
        input.setAttribute('aria-readonly', 'true');
        input.setAttribute('aria-disabled', 'true');
      });
      row.querySelectorAll(GIFT_BUTTON_SELECTOR).forEach((button) => {
        button.disabled = true;
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
      });
    });
  }
  function blockGiftQuantityChange(event) {
    const control = event.target?.closest?.(`${GIFT_BUTTON_SELECTOR}, ${GIFT_INPUT_SELECTOR}`);
    const row = control?.closest(GIFT_ROW_SELECTOR);
    if (!row || !giftRowState(row)) return;
    // Keep keyboard navigation available; readonly already prevents text edits.
    if (
      event.type === 'keydown' &&
      !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Enter', '+', '-'].includes(event.key)
    )
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    protectGiftQuantities();
  }
  for (const name of ['click', 'input', 'change', 'keydown']) {
    document.addEventListener(name, blockGiftQuantityChange, true);
  }
  let refreshPending = false;
  function refreshSlideCart() {
    if (refreshPending || !document.querySelector('cart-drawer')) return;
    refreshPending = true;
    queueMicrotask(async () => {
      try {
        await refreshCustomDrawer();
      } catch (refreshError) {
        error(refreshError);
      } finally {
        refreshPending = false;
      }
    });
  }
  let exclusiveTransactionInFlight = false;
  let reconcileRequestedDuringTransaction = false;
  let reconciling = false;
  let reconcileTimer = null;
  let reconcileRequestedWhileRunning = false;
  function scheduleReconcile(delay = RECONCILE_DELAY_MS, reason = 'cart_update') {
    if (exclusiveTransactionInFlight) {
      reconcileRequestedDuringTransaction = true;
      return;
    }
    if (reconciling) {
      reconcileRequestedWhileRunning = true;
      return;
    }
    if (reconcileTimer !== null) {
      if (delay !== 0) return;
      clearTimeout(reconcileTimer);
    }
    reconcileTimer = setTimeout(() => {
      reconcileTimer = null;
      reconcileGwps(reason);
    }, delay);
  }
  async function runExclusiveTransaction(operation) {
    exclusiveTransactionInFlight = true;
    try {
      return await operation();
    } finally {
      exclusiveTransactionInFlight = false;
      if (reconcileRequestedDuringTransaction) {
        reconcileRequestedDuringTransaction = false;
        scheduleReconcile(VERIFY_AFTER_WRITE_MS, 'after_transaction');
      }
    }
  }
  async function reconcileGwps() {
    if (exclusiveTransactionInFlight) {
      reconcileRequestedDuringTransaction = true;
      return;
    }
    if (reconciling) {
      reconcileRequestedWhileRunning = true;
      return;
    }
    reconciling = true;
    try {
      const cart = await fetchStableCart();
      const plan = planMcdReconciliation(cart);
      if (Object.keys(plan.updates).length) {
        await updateCart({ updates: plan.updates });
        refreshSlideCart();
      }
      protectGiftQuantities();
    } catch (reconcileError) {
      error('McD validation failed', reconcileError);
    } finally {
      reconciling = false;
      if (reconcileRequestedWhileRunning) {
        reconcileRequestedWhileRunning = false;
        scheduleReconcile(VERIFY_AFTER_WRITE_MS, 'followup');
      }
    }
  }
  async function handleCustomMcdCheckout(event) {
    const isCheckout =
      event.type === 'submit' ? event.submitter && isCheckoutTrigger(event.submitter) : isCheckoutTrigger(event.target);
    if (!isCheckout) return;
    if (
      !document.querySelector(
        '[data-mcd-gift="true"], [data-variant-id="56860353396902"], [data-variant-id="46653206495398"]',
      ) &&
      !exclusiveTransactionInFlight
    )
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (exclusiveTransactionInFlight || reconciling) return;
    try {
      await runExclusiveTransaction(async () => {
        const cart = await fetchStableCart();
        const plan = planMcdReconciliation(cart);
        if (Object.keys(plan.updates).length) {
          await updateCart({ updates: plan.updates });
          refreshSlideCart('mcd_checkout_cleanup');
          window.alert?.('Your promotional gift was updated. Please review your cart before checking out.');
          return;
        }
        // Allow the external team's checkout handler to validate its WWT state.
        customCheckoutApproved = true;
        try {
          if (event.type === 'click') event.target.closest("a, button, input[type='submit']").click();
          else event.target.requestSubmit(event.submitter);
        } finally {
          customCheckoutApproved = false;
        }
      });
    } catch (checkoutError) {
      error('McD checkout validation failed', checkoutError);
      window.alert?.('Please try checkout again. Your promotional gift could not be verified.');
    }
  }

  let customCheckoutApproved = false;
  function customCheckoutCapture(event) {
    if (!customCheckoutApproved) handleCustomMcdCheckout(event);
  }
  document.addEventListener('click', customCheckoutCapture, true);
  document.addEventListener('submit', customCheckoutCapture, true);
  for (const name of ['cart:morphed', 'cart:added', 'cart:updated', 'cart:changed', 'cart:refresh']) {
    document.addEventListener(name, () => {
      protectGiftQuantities();
      scheduleReconcile();
    });
  }
  window.WooblesCartGWP = {
    redeemMcdPromo,
    refreshCart: refreshSlideCart,
    requestReconcile(reason = 'external_request') {
      scheduleReconcile(0, reason);
    },
    async runCartTransaction(operation) {
      if (typeof operation !== 'function') throw new TypeError('Cart transaction requires a function.');
      if (exclusiveTransactionInFlight || reconciling) {
        const busyError = new Error('Please wait for your cart to finish updating, then try again.');
        busyError.code = 'CART_BUSY';
        throw busyError;
      }
      return runExclusiveTransaction(async () => {
        try {
          return await operation({ readCart: fetchStableCart, addItems, updateCart });
        } finally {
          refreshSlideCart();
          scheduleReconcile(VERIFY_AFTER_WRITE_MS);
        }
      });
    },
  };
  document.dispatchEvent(new CustomEvent('woobles:gwp-manager-ready'));
  function init() {
    protectGiftQuantities();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (Array.isArray(event?.cartData?.items)) latestProtectionCart = event.cartData;
        protectGiftQuantities();
        scheduleReconcile();
      });
    }
    if (document.querySelector('cart-drawer, cart-items')) scheduleReconcile(0, 'init');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
